import {ApolloClientProviderImpl} from '../ApolloClientProvider';
import {AppearanceImpl} from '../Appearance';
import {AppearanceStateImpl} from '../AppearanceState';
import {AppVisibilityImpl} from '../AppVisibility';
import {AppVisibilityStateImpl} from '../AppVisibilityState';
import {CurrentTimeImpl} from '../CurrentTime';
import type {FavoritesRecord} from '../Favorites';
import JsonParserImpl from '../Json/JsonParserImpl';
import JsonStringifierImpl from '../Json/JsonStringifierImpl';
import type {JsonKeyValueMap} from '../JsonKeyValueMap';
import {JsonKeyValueStorageImpl} from '../JsonKeyValueStorage';
import KeyValueStorageImpl from '../KeyValueStorage/KeyValueStorageImpl';
import {LocatorSharingStateImpl} from '../LocatorSharingState';
import type {ProfileRecord} from '../Profile';
import ScheduleExporterImpl from '../ScheduleExporter/ScheduleExporterImpl';
import SingleValueStorageImpl from '../SingleValueStorage/SingleValueStorageImpl';
import {StaticConfigurationProviderImpl} from '../StaticConfigurationProvider';
import {defaultThemeRecord, type ThemeRecord} from '../ThemeRecord';
import ThemeStateImpl from '../ThemeState/ThemeStateImpl';
import {TimeImpl} from '../Time';
import {TimeStateImpl} from '../TimeState';
import type {Root} from './Root';

export default class RootImpl implements Root, Disposable {
  readonly time = new TimeImpl();
  readonly appVisibility = new AppVisibilityImpl();
  readonly appVisibilityState = new AppVisibilityStateImpl(this);
  readonly timeState = new TimeStateImpl(this);
  readonly currentTime = new CurrentTimeImpl(this);
  readonly jsonParser = new JsonParserImpl();
  readonly jsonStringifier = new JsonStringifierImpl();
  readonly keyValueStorage = new KeyValueStorageImpl();
  readonly jsonKeyValueStorage = new JsonKeyValueStorageImpl<JsonKeyValueMap>(
    this,
    this.keyValueStorage,
    {prefix: 'local'},
  );
  readonly profileStorage = SingleValueStorageImpl.create<
    'profile',
    ProfileRecord
  >(this, 'profile');
  readonly appearance = new AppearanceImpl();
  readonly appearanceState = new AppearanceStateImpl(this);
  readonly themeStorage = SingleValueStorageImpl.create<'theme', ThemeRecord>(
    this,
    'theme',
    defaultThemeRecord,
  );
  readonly favoritesStorage = SingleValueStorageImpl.create<
    'favorites',
    FavoritesRecord
  >(this, 'favorites');
  readonly themeState = new ThemeStateImpl(this);
  readonly staticConfigurationProvider = new StaticConfigurationProviderImpl();
  readonly apolloClientProvider = new ApolloClientProviderImpl(this);
  readonly scheduleExporter = new ScheduleExporterImpl();
  readonly locatorSharingState = new LocatorSharingStateImpl();

  [Symbol.dispose]() {
    const stack = new DisposableStack();
    for (const prop in this) {
      if (!Object.prototype.hasOwnProperty.call(this, prop)) {
        continue;
      }
      const instance = this[prop];
      if (
        typeof instance === 'object' &&
        instance !== null &&
        isDisposable(instance)
      ) {
        stack.use(instance);
      }
    }
    stack.dispose();
  }
}

function isDisposable(_: object): _ is Disposable {
  const dispose: unknown = Reflect.get(_, Symbol.dispose, _);
  return typeof dispose === 'function';
}
